import type { TaskRun } from "../../../../shared/types";
import type { WorkflowContext, WorkflowStepModule } from "../types";

export type ValidationVerdict = "strong" | "viable" | "weak";

export type ContentValidationSnapshot = {
  verdict: ValidationVerdict;
  summary: string;
  personaLabel: string | null;
  painpoints: string[];
  languageMatches: string[];
  recommendedRewrite: string | null;
  proofRequirements: string[];
};

function metadataRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    : [];
}

export function extractContentValidationSnapshot(
  run: TaskRun | null | undefined,
): ContentValidationSnapshot | null {
  const output = metadataRecord(run?.output);
  if (!output) return null;
  const verdict = output.verdict;
  if (verdict !== "strong" && verdict !== "viable" && verdict !== "weak") return null;

  const persona = metadataRecord(output.validated_persona);
  const painpoints = Array.isArray(output.validated_painpoints)
    ? output.validated_painpoints.flatMap((item) => {
        const record = metadataRecord(item);
        const label = record && typeof record.label === "string" ? record.label.trim() : "";
        return label ? [label] : [];
      })
    : [];
  const languageMatches = Array.isArray(output.language_matches)
    ? output.language_matches.flatMap((item) => {
        const record = metadataRecord(item);
        const phrase = record && typeof record.phrase === "string" ? record.phrase.trim() : "";
        return phrase ? [phrase] : [];
      })
    : [];

  return {
    verdict,
    summary: typeof output.validation_summary === "string"
      ? output.validation_summary
      : typeof output.summary === "string"
        ? output.summary
        : "Validation completed.",
    personaLabel: persona && typeof persona.label === "string" ? persona.label : null,
    painpoints,
    languageMatches,
    recommendedRewrite: typeof output.recommended_rewrite === "string" ? output.recommended_rewrite : null,
    proofRequirements: stringArray(output.proof_requirements),
  };
}

function canValidateBrief(state: string): boolean {
  return ["ideation", "researching", "briefed"].includes(state);
}

export const contentValidationStep: WorkflowStepModule<ContentValidationSnapshot> = {
  id: "content_validation",
  label: "Content validation",
  runButtonLabel: "Validate angle",
  runningLabel: "Validating…",
  renderer: "validation",
  taskSlug: (context: WorkflowContext) => `content_validation:${context.brief.id}`,
  isEligible: (context: WorkflowContext) =>
    canValidateBrief(context.brief.state)
      ? { eligible: true }
      : { eligible: false, reason: "Available while the brief is still being shaped." },
  buildParams: (context: WorkflowContext) => ({
    brief_id: context.brief.id,
    campaign_id: context.campaignId,
    surface: context.brief.surface ?? null,
    platform: context.brief.platform ?? null,
    content_type: context.brief.format ?? null,
    candidate_hook: context.brief.title,
    candidate_angle: context.campaign?.goal ?? null,
    persona_hypothesis: context.brand?.strategy?.audience?.who ?? context.brand?.target_audience ?? null,
    painpoint_hypothesis: context.campaign?.strategy_pillar ?? null,
    language_seeds: [context.brief.title],
  }),
  extractSnapshot: extractContentValidationSnapshot,
};
